import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { casesAPI } from '../services/api';

const Cases = () => {
  const navigate = useNavigate();
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCases = async () => {
      try {
        setLoading(true);
        const casesData = await casesAPI.getAll();
        setCaseStudies(casesData);
      } catch (err) {
        console.error('Error fetching cases:', err);
        setError('Ошибка загрузки кейсов');
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, []);

  if (loading) {
    return (
      <div className="container" style={{ padding: 'var(--space-xxxl) var(--space-l)', textAlign: 'center' }}>
        <p>Загрузка...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ padding: 'var(--space-xxxl) var(--space-l)', textAlign: 'center' }}>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section style={{
        background: `linear-gradient(135deg, rgba(212, 196, 176, 0.15) 0%, rgba(250, 249, 247, 1) 100%)`,
        padding: 'var(--space-xxxl) var(--space-l)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 style={{ marginBottom: 'var(--space-l)' }}>Трансформации</h1>
          <p style={{
            fontSize: '1.125rem',
            color: 'var(--warm-gray)',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.7'
          }}>
            Реальные истории женщин, которые нашли свой стиль и уверенность. Каждый кейс — это путь от неопределенности к ясности.
          </p>
        </div>
      </section>

      {/* Cases Grid */}
      <section className="section">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 'var(--space-xxl)'
          }}>
            {caseStudies.map((caseStudy) => (
              <Link
                key={caseStudy.id}
                to={`/cases/${caseStudy.id}`}
                style={{ textDecoration: 'none' }}
              >
                <div className="card case-card" style={{
                  height: '100%',
                  transition: 'transform 400ms ease, box-shadow 400ms ease'
                }}>
                  {/* Before/After Images */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 'var(--space-m)',
                    marginBottom: 'var(--space-l)'
                  }}>
                    <div style={{ position: 'relative' }}>
                      <div style={{
                        height: '240px',
                        borderRadius: 'var(--radius-sm)',
                        overflow: 'hidden'
                      }}>
                        <img
                          src={caseStudy.before_image_url}
                          alt="До"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            filter: 'grayscale(30%)',
                            transition: 'transform 500ms ease'
                          }}
                          className="case-image"
                        />
                      </div>
                      <div style={{
                        position: 'absolute',
                        bottom: 'var(--space-s)',
                        left: 'var(--space-s)',
                        backgroundColor: 'rgba(44, 44, 44, 0.8)',
                        color: 'var(--cream-white)',
                        padding: 'var(--space-xs) var(--space-m)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        letterSpacing: '1px',
                        textTransform: 'uppercase'
                      }}>
                        До
                      </div>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <div style={{
                        height: '240px',
                        borderRadius: 'var(--radius-sm)',
                        overflow: 'hidden'
                      }}>
                        <img
                          src={caseStudy.after_image_url}
                          alt="После"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 500ms ease'
                          }}
                          className="case-image"
                        />
                      </div>
                      <div style={{
                        position: 'absolute',
                        bottom: 'var(--space-s)',
                        right: 'var(--space-s)',
                        backgroundColor: 'var(--warm-sage)',
                        color: 'var(--cream-white)',
                        padding: 'var(--space-xs) var(--space-m)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        letterSpacing: '1px',
                        textTransform: 'uppercase'
                      }}>
                        После
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 style={{
                    marginBottom: 'var(--space-s)',
                    color: 'var(--charcoal)',
                    fontSize: '1.5rem'
                  }}>
                    {caseStudy.client_name}, {caseStudy.age}
                  </h3>
                  <p style={{
                    color: 'var(--warm-gray)',
                    fontSize: '0.875rem',
                    marginBottom: 'var(--space-m)',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    {caseStudy.city}
                  </p>
                  <p style={{
                    color: 'var(--warm-gray)',
                    marginBottom: 'var(--space-l)',
                    fontSize: '1.0625rem',
                    lineHeight: '1.6'
                  }}>
                    {caseStudy.challenge}
                  </p>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: 'var(--space-l)',
                    borderTop: '1px solid var(--soft-gray)'
                  }}>
                    <span style={{
                      fontSize: '0.875rem',
                      color: 'var(--warm-sage)',
                      fontWeight: 500,
                      letterSpacing: '1px',
                      textTransform: 'uppercase'
                    }}>
                      Читать историю
                    </span>
                    <ArrowRight size={20} style={{ color: 'var(--warm-sage)' }} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-bg-gray">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ marginBottom: 'var(--space-l)' }}>
            Готовы к переменам?
          </h2>
          <p style={{
            fontSize: '1.0625rem',
            color: 'var(--warm-gray)',
            maxWidth: '600px',
            margin: '0 auto var(--space-xl)',
            lineHeight: '1.6'
          }}>
            Начните свой путь к уверенности в стиле. Оставьте заявку, и мы обсудим, как я могу помочь именно вам.
          </p>
          <button
            className="btn-primary"
            onClick={() => navigate({ pathname: '/', hash: '#contact' })}
          >
            Оставить заявку
          </button>
        </div>
      </section>

      <style>{`
        .case-card:hover {
          transform: translateY(-8px);
        }
        .case-card:hover .case-image {
          transform: scale(1.05);
        }
      `}</style>
    </div>
  );
};

export default Cases;
